import { Column, Entity, Index } from 'typeorm';
import type { StorefrontTextMessageKind } from '../../utils/storefront-messages';
import { BaseEntity } from './base.entity';

/**
 * PD-17377 — one storefront's wording for one of its account texts
 * (welcome, password reset, sign-in code, and the Android app's sign-in code
 * text, PD-17379). A storefront is a brand, so each
 * brand owns one row per kind; the rows are generated with today's built-in
 * wording when the brand is created and edited on the brand's page.
 *
 * crm-api fills the `{{variables}}` when it sends the text. A missing row, or
 * one that no longer passes the save rules in validateStorefrontTextTemplate,
 * sends the built-in wording instead, so a template can never stop a customer
 * from signing in.
 *
 * The unique index makes generation safe to run twice: a second insert for
 * the same brand and kind fails instead of leaving two texts to choose from.
 * Rows are never soft-deleted (a restore rewrites the body), so the index has
 * no deleted rows to trip over.
 */
@Index('UK_brand_text_template_brand_kind', ['fkBrandId', 'kind'], {
	unique: true,
})
@Entity('brand_text_template')
export class BrandTextTemplateEntity extends BaseEntity {
	@Column('int', { name: 'fk_brand__id', unsigned: true })
	public fkBrandId!: number;

	@Column('varchar', { name: 'kind', length: 40 })
	public kind!: StorefrontTextMessageKind;

	@Column('text', { name: 'body' })
	public body!: string;

	/**
	 * PD-17379 — only the Android app sign-in text reads it: off, the app is sent
	 * the sign-in code text instead. On for every other text.
	 */
	@Column('boolean', { name: 'isEnabled', default: true })
	public isEnabled!: boolean;
}
